import React, { useEffect, useState } from 'react';
import Card from '../components/ui/Card';
import StatusBadge from '../components/ui/StatusBadge';
import { BarChart3Icon, TrendingUpIcon, UsersIcon, BuildingIcon, QrCodeIcon, CreditCardIcon, ChevronRightIcon, ArrowRightIcon, PieChartIcon, MapPinIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, InfoIcon, HelpCircleIcon } from 'lucide-react';
// Mock data for the dashboard
const merchantStats = {
  total: 5432,
  active: 4890,
  inactive: 542,
  pending: 124,
  suspended: 35,
  growth: 12.5
};
// Pakistani regions data
const regionData = [{
  name: 'Karachi',
  value: 42,
  merchants: 2281,
  transactions: 35250,
  growth: 15.3
}, {
  name: 'Lahore',
  value: 28,
  merchants: 1521,
  transactions: 23100,
  growth: 12.7
}, {
  name: 'Islamabad',
  value: 12,
  merchants: 652,
  transactions: 10250,
  growth: 18.2
}, {
  name: 'Peshawar',
  value: 10,
  merchants: 543,
  transactions: 8540,
  growth: 9.5
}, {
  name: 'Faisalabad',
  value: 8,
  merchants: 435,
  transactions: 7320,
  growth: 11.2
}];
// Transaction trends data by time period
const transactionTrendsData = {
  day: [{
    date: '9AM',
    value: 450000
  }, {
    date: '10AM',
    value: 520000
  }, {
    date: '11AM',
    value: 640000
  }, {
    date: '12PM',
    value: 780000
  }, {
    date: '1PM',
    value: 890000
  }, {
    date: '2PM',
    value: 750000
  }, {
    date: '3PM',
    value: 680000
  }, {
    date: '4PM',
    value: 720000
  }, {
    date: '5PM',
    value: 850000
  }, {
    date: '6PM',
    value: 920000
  }],
  week: [{
    date: 'Mon',
    value: 4250000
  }, {
    date: 'Tue',
    value: 3980000
  }, {
    date: 'Wed',
    value: 4520000
  }, {
    date: 'Thu',
    value: 4780000
  }, {
    date: 'Fri',
    value: 5240000
  }, {
    date: 'Sat',
    value: 6120000
  }, {
    date: 'Sun',
    value: 5680000
  }],
  month: [{
    date: 'Week 1',
    value: 28500000
  }, {
    date: 'Week 2',
    value: 31200000
  }, {
    date: 'Week 3',
    value: 33800000
  }, {
    date: 'Week 4',
    value: 36500000
  }],
  year: [{
    date: 'Jan',
    value: 120500000
  }, {
    date: 'Feb',
    value: 115800000
  }, {
    date: 'Mar',
    value: 132400000
  }, {
    date: 'Apr',
    value: 128900000
  }, {
    date: 'May',
    value: 142500000
  }, {
    date: 'Jun',
    value: 149800000
  }, {
    date: 'Jul',
    value: 156200000
  }, {
    date: 'Aug',
    value: 162500000
  }, {
    date: 'Sep',
    value: 168900000
  }, {
    date: 'Oct',
    value: 175200000
  }, {
    date: 'Nov',
    value: 182500000
  }, {
    date: 'Dec',
    value: 195800000
  }]
};
// Payment method distribution
const paymentMethodData = [{
  method: 'Credit Card',
  percentage: 45,
  growth: 5.2,
  transactions: 38250
}, {
  method: 'QR Payments',
  percentage: 30,
  growth: 18.5,
  transactions: 25500
}, {
  method: 'Bank Transfer',
  percentage: 15,
  growth: 3.7,
  transactions: 12750
}, {
  method: 'Mobile Wallet',
  percentage: 10,
  growth: 12.3,
  transactions: 8500
}];
const transactionStats = {
  volume: 12568000,
  count: 85432,
  averageValue: 147.11,
  growth: 8.7
};
const pendingActions = [{
  id: 1,
  type: 'Merchant Onboarding',
  name: 'Bismillah Traders',
  status: 'pending',
  time: '2 hours ago'
}, {
  id: 2,
  type: 'Document Review',
  name: 'Zeen Textile',
  status: 'pending',
  time: '5 hours ago'
}, {
  id: 3,
  type: 'RTP Request',
  name: 'Islamabad Metro',
  status: 'pending',
  time: '1 day ago'
}, {
  id: 4,
  type: 'Dispute Resolution',
  name: 'Orient Online Shop',
  status: 'urgent',
  time: '2 days ago'
}];
const Dashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [currentTransactionData, setCurrentTransactionData] = useState(transactionTrendsData.month);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<{
    visible: boolean;
    content: string;
    x: number;
    y: number;
  }>({
    visible: false,
    content: '',
    x: 0,
    y: 0
  });
  // Update transaction data when time range changes
  useEffect(() => {
    setCurrentTransactionData(transactionTrendsData[timeRange]);
  }, [timeRange]);
  // Calculate percentages for merchant status
  const getMerchantStatusPercentage = (status: keyof typeof merchantStats) => {
    if (status === 'total' || status === 'growth') return 0;
    return Math.round(merchantStats[status] / merchantStats.total * 100);
  };
  // Get status badge for each status
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-2201" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-2202" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-2203" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-2204" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-2205" />;
    }
  };
  // Get status icon for each status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-2206" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-2207" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-2208" />;
      case 'suspended':
        return <AlertCircleIcon className="h-5 w-5 text-blue-500" data-id="element-2209" />;
      default:
        return null;
    }
  };
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  // Handle region selection
  const handleRegionClick = (regionName: string) => {
    setSelectedRegion(selectedRegion === regionName ? null : regionName);
  };
  // Handle payment method selection
  const handlePaymentMethodClick = (methodName: string) => {
    setSelectedPaymentMethod(selectedPaymentMethod === methodName ? null : methodName);
  };
  // Get region details
  const getRegionDetails = () => {
    if (!selectedRegion) return null;
    const region = regionData.find(r => r.name === selectedRegion);
    if (!region) return null;
    return <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg" data-id="element-2210">
        <h4 className="font-medium text-blue-800 dark:text-blue-300" data-id="element-2211">
          {region.name} Region Details
        </h4>
        <div className="grid grid-cols-2 gap-3 mt-2" data-id="element-2212">
          <div data-id="element-2213">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2214">
              Active Merchants
            </p>
            <p className="text-lg font-medium" data-id="element-2215">
              {region.merchants.toLocaleString()}
            </p>
          </div>
          <div data-id="element-2216">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2217">
              Monthly Transactions
            </p>
            <p className="text-lg font-medium" data-id="element-2218">
              {region.transactions.toLocaleString()}
            </p>
          </div>
          <div data-id="element-2219">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2220">
              Growth Rate
            </p>
            <p className="text-lg font-medium text-green-600 dark:text-green-400" data-id="element-2221">
              +{region.growth}%
            </p>
          </div>
          <div data-id="element-2222">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2223">
              Market Share
            </p>
            <p className="text-lg font-medium" data-id="element-2224">{region.value}%</p>
          </div>
        </div>
        <button className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2225">
          View full region report
          <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2226" />
        </button>
      </div>;
  };
  // Get payment method details
  const getPaymentMethodDetails = () => {
    if (!selectedPaymentMethod) return null;
    const method = paymentMethodData.find(m => m.method === selectedPaymentMethod);
    if (!method) return null;
    return <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-2227">
        <h4 className="font-medium text-gray-800 dark:text-gray-300" data-id="element-2228">
          {method.method} Details
        </h4>
        <div className="grid grid-cols-2 gap-3 mt-2" data-id="element-2229">
          <div data-id="element-2230">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2231">
              Market Share
            </p>
            <p className="text-lg font-medium" data-id="element-2232">{method.percentage}%</p>
          </div>
          <div data-id="element-2233">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2234">
              Monthly Transactions
            </p>
            <p className="text-lg font-medium" data-id="element-2235">
              {method.transactions.toLocaleString()}
            </p>
          </div>
          <div data-id="element-2236">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2237">
              Growth Rate
            </p>
            <p className="text-lg font-medium text-green-600 dark:text-green-400" data-id="element-2238">
              +{method.growth}%
            </p>
          </div>
          <div data-id="element-2239">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2240">
              Avg. Transaction Value
            </p>
            <p className="text-lg font-medium" data-id="element-2241">
              {formatCurrency(transactionStats.volume * (method.percentage / 100) / method.transactions)}
            </p>
          </div>
        </div>
        <button className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2242">
          View payment method analytics
          <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2243" />
        </button>
      </div>;
  };
  // Show tooltip on hover
  const handleTooltipShow = (content: string, event: React.MouseEvent) => {
    setShowTooltip({
      visible: true,
      content,
      x: event.clientX,
      y: event.clientY
    });
  };
  // Hide tooltip
  const handleTooltipHide = () => {
    setShowTooltip({
      ...showTooltip,
      visible: false
    });
  };
  return <div className="space-y-6" data-id="element-2244">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-2245">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2246">
          Dashboard
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-2" data-id="element-2247">
          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2248">
            Time Range:
          </span>
          <div className="inline-flex rounded-md shadow-sm" data-id="element-2249">
            {(['day', 'week', 'month', 'year'] as const).map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-sm font-medium ${timeRange === range ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'} ${range === 'day' ? 'rounded-l-md' : ''} ${range === 'year' ? 'rounded-r-md' : ''} border border-gray-300 dark:border-gray-600`} data-id="element-2250">
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>)}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2251">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-800" data-id="element-2252">
          <div className="flex items-center" data-id="element-2253">
            <div className="p-3 rounded-full bg-blue-500 text-white mr-4" data-id="element-2254">
              <BuildingIcon className="h-6 w-6" data-id="element-2255" />
            </div>
            <div data-id="element-2256">
              <p className="text-sm font-medium text-blue-600 dark:text-blue-400" data-id="element-2257">
                Total Merchants
              </p>
              <div className="flex items-baseline" data-id="element-2258">
                <p className="text-2xl font-semibold text-blue-800 dark:text-blue-300" data-id="element-2259">
                  {merchantStats.total.toLocaleString()}
                </p>
                <span className="ml-2 text-sm text-green-600 dark:text-green-400 flex items-center" data-id="element-2260">
                  <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-2261" />
                  {merchantStats.growth}%
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-between text-sm" data-id="element-2262">
            <div data-id="element-2263">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2264">Active</p>
              <p className="font-medium" data-id="element-2265">
                {merchantStats.active.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2266">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2267">Inactive</p>
              <p className="font-medium" data-id="element-2268">
                {merchantStats.inactive.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2269">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2270">Pending</p>
              <p className="font-medium" data-id="element-2271">
                {merchantStats.pending.toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 border-green-200 dark:border-green-800" data-id="element-2272">
          <div className="flex items-center" data-id="element-2273">
            <div className="p-3 rounded-full bg-green-500 text-white mr-4" data-id="element-2274">
              <CreditCardIcon className="h-6 w-6" data-id="element-2275" />
            </div>
            <div data-id="element-2276">
              <p className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-2277">
                Transaction Volume
              </p>
              <div className="flex items-baseline" data-id="element-2278">
                <p className="text-2xl font-semibold text-green-800 dark:text-green-300" data-id="element-2279">
                  {formatCurrency(transactionStats.volume)}
                </p>
                <span className="ml-2 text-sm text-green-600 dark:text-green-400 flex items-center" data-id="element-2280">
                  <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-2281" />
                  {transactionStats.growth}%
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-between text-sm" data-id="element-2282">
            <div data-id="element-2283">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2284">Count</p>
              <p className="font-medium" data-id="element-2285">
                {transactionStats.count.toLocaleString()}
              </p>
            </div>
            <div data-id="element-2286">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2287">Avg Value</p>
              <p className="font-medium" data-id="element-2288">
                PKR {transactionStats.averageValue.toFixed(2)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-200 dark:border-purple-800" data-id="element-2289">
          <div className="flex items-center" data-id="element-2290">
            <div className="p-3 rounded-full bg-purple-500 text-white mr-4" data-id="element-2291">
              <QrCodeIcon className="h-6 w-6" data-id="element-2292" />
            </div>
            <div data-id="element-2293">
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-2294">
                QR Scans
              </p>
              <div className="flex items-baseline" data-id="element-2295">
                <p className="text-2xl font-semibold text-purple-800 dark:text-purple-300" data-id="element-2296">
                  28,429
                </p>
                <span className="ml-2 text-sm text-green-600 dark:text-green-400 flex items-center" data-id="element-2297">
                  <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-2298" />
                  5.3%
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-between text-sm" data-id="element-2299">
            <div data-id="element-2300">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2301">Static QR</p>
              <p className="font-medium" data-id="element-2302">18,245</p>
            </div>
            <div data-id="element-2303">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2304">Dynamic QR</p>
              <p className="font-medium" data-id="element-2305">10,184</p>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 border-orange-200 dark:border-orange-800" data-id="element-2306">
          <div className="flex items-center" data-id="element-2307">
            <div className="p-3 rounded-full bg-orange-500 text-white mr-4" data-id="element-2308">
              <UsersIcon className="h-6 w-6" data-id="element-2309" />
            </div>
            <div data-id="element-2310">
              <p className="text-sm font-medium text-orange-600 dark:text-orange-400" data-id="element-2311">
                Active Users
              </p>
              <div className="flex items-baseline" data-id="element-2312">
                <p className="text-2xl font-semibold text-orange-800 dark:text-orange-300" data-id="element-2313">
                  182
                </p>
                <span className="ml-2 text-sm text-green-600 dark:text-green-400 flex items-center" data-id="element-2314">
                  <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-2315" />
                  2.1%
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-between text-sm" data-id="element-2316">
            <div data-id="element-2317">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2318">Admins</p>
              <p className="font-medium" data-id="element-2319">24</p>
            </div>
            <div data-id="element-2320">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2321">Managers</p>
              <p className="font-medium" data-id="element-2322">68</p>
            </div>
            <div data-id="element-2323">
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2324">Agents</p>
              <p className="font-medium" data-id="element-2325">90</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Merchant Status Overview */}
      <Card title="Merchant Status Overview" data-id="element-2326">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2327">
          <div data-id="element-2328">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4" data-id="element-2329">
              Status Distribution
            </h3>
            <div className="space-y-4" data-id="element-2330">
              <div data-id="element-2331">
                <div className="flex items-center justify-between mb-1" data-id="element-2332">
                  <div className="flex items-center" data-id="element-2333">
                    {getStatusIcon('active')}
                    <span className="ml-2 text-sm font-medium" data-id="element-2334">
                      Active Merchants
                    </span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-2335">
                    {merchantStats.active.toLocaleString()}
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-2336">
                      ({getMerchantStatusPercentage('active')}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2337">
                  <div className="bg-green-500 h-2.5 rounded-full" style={{
                  width: `${getMerchantStatusPercentage('active')}%`
                }} data-id="element-2338"></div>
                </div>
              </div>
              <div data-id="element-2339">
                <div className="flex items-center justify-between mb-1" data-id="element-2340">
                  <div className="flex items-center" data-id="element-2341">
                    {getStatusIcon('pending')}
                    <span className="ml-2 text-sm font-medium" data-id="element-2342">
                      Pending Merchants
                    </span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-2343">
                    {merchantStats.pending.toLocaleString()}
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-2344">
                      ({getMerchantStatusPercentage('pending')}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2345">
                  <div className="bg-yellow-500 h-2.5 rounded-full" style={{
                  width: `${getMerchantStatusPercentage('pending')}%`
                }} data-id="element-2346"></div>
                </div>
              </div>
              <div data-id="element-2347">
                <div className="flex items-center justify-between mb-1" data-id="element-2348">
                  <div className="flex items-center" data-id="element-2349">
                    {getStatusIcon('inactive')}
                    <span className="ml-2 text-sm font-medium" data-id="element-2350">
                      Inactive Merchants
                    </span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-2351">
                    {merchantStats.inactive.toLocaleString()}
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-2352">
                      ({getMerchantStatusPercentage('inactive')}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2353">
                  <div className="bg-red-500 h-2.5 rounded-full" style={{
                  width: `${getMerchantStatusPercentage('inactive')}%`
                }} data-id="element-2354"></div>
                </div>
              </div>
              <div data-id="element-2355">
                <div className="flex items-center justify-between mb-1" data-id="element-2356">
                  <div className="flex items-center" data-id="element-2357">
                    {getStatusIcon('suspended')}
                    <span className="ml-2 text-sm font-medium" data-id="element-2358">
                      Suspended Merchants
                    </span>
                  </div>
                  <span className="text-sm font-medium" data-id="element-2359">
                    {merchantStats.suspended.toLocaleString()}
                    <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-2360">
                      ({getMerchantStatusPercentage('suspended')}%)
                    </span>
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5" data-id="element-2361">
                  <div className="bg-blue-500 h-2.5 rounded-full" style={{
                  width: `${getMerchantStatusPercentage('suspended')}%`
                }} data-id="element-2362"></div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-2363">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4" data-id="element-2364">
              Recent Status Changes
            </h3>
            <div className="space-y-3" data-id="element-2365">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-2366">
                <div className="flex justify-between items-center" data-id="element-2367">
                  <span className="font-medium text-sm" data-id="element-2368">Bismillah Traders</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2369">
                    Today, 10:30 AM
                  </span>
                </div>
                <div className="flex items-center mt-1" data-id="element-2370">
                  <span className="inline-block" data-id="element-2371">
                    {getStatusBadge('pending')}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-2372" />
                  <span className="inline-block" data-id="element-2373">
                    {getStatusBadge('active')}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-2374">
                <div className="flex justify-between items-center" data-id="element-2375">
                  <span className="font-medium text-sm" data-id="element-2376">
                    Orient Online Shop
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2377">
                    Yesterday, 4:15 PM
                  </span>
                </div>
                <div className="flex items-center mt-1" data-id="element-2378">
                  <span className="inline-block" data-id="element-2379">
                    {getStatusBadge('active')}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-2380" />
                  <span className="inline-block" data-id="element-2381">
                    {getStatusBadge('suspended')}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-2382">
                <div className="flex justify-between items-center" data-id="element-2383">
                  <span className="font-medium text-sm" data-id="element-2384">Ginsoy Lahore</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2385">
                    Oct 15, 2023
                  </span>
                </div>
                <div className="flex items-center mt-1" data-id="element-2386">
                  <span className="inline-block" data-id="element-2387">
                    {getStatusBadge('active')}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-2388" />
                  <span className="inline-block" data-id="element-2389">
                    {getStatusBadge('inactive')}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-between" data-id="element-2390">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2391">
                View All Status Changes
                <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2392" />
              </button>
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2393">
                Manage Merchants
                <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2394" />
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2395">
        <Card className="lg:col-span-2" data-id="element-2396">
          <div className="flex justify-between items-center mb-4" data-id="element-2397">
            <div className="flex items-center" data-id="element-2398">
              <h3 className="font-medium" data-id="element-2399">Transaction Trends ({timeRange})</h3>
              <button className="ml-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400" onMouseEnter={e => handleTooltipShow('Shows transaction volume over time. Click on time range buttons to change the view.', e)} onMouseLeave={handleTooltipHide} data-id="element-2400">
                <HelpCircleIcon className="h-4 w-4" data-id="element-2401" />
              </button>
            </div>
            <div className="flex items-center space-x-2" data-id="element-2402">
              <select className="text-sm border border-gray-300 dark:border-gray-600 rounded-md p-1 bg-white dark:bg-gray-800" data-id="element-2403">
                <option data-id="element-2404">All Payment Methods</option>
                <option data-id="element-2405">POS</option>
                <option data-id="element-2406">QR</option>
                <option data-id="element-2407">Online</option>
                <option data-id="element-2408">RTP</option>
              </select>
              <button className="p-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" data-id="element-2409">
                <BarChart3Icon className="h-4 w-4" data-id="element-2410" />
              </button>
            </div>
          </div>
          <div className="h-64 relative" data-id="element-2411">
            <div className="absolute inset-0" data-id="element-2412">
              <div className="h-full flex items-end" data-id="element-2413">
                {currentTransactionData.map((item, index) => <div key={index} className="flex-1 flex flex-col items-center justify-end h-full group" data-id="element-2414">
                    <div className="w-full max-w-[30px] bg-blue-500 dark:bg-blue-600 group-hover:bg-blue-600 dark:group-hover:bg-blue-500 rounded-t-sm mx-auto transition-all duration-200" style={{
                  height: `${item.value / Math.max(...currentTransactionData.map(d => d.value)) * 80}%`,
                  minHeight: '10%'
                }} onMouseEnter={e => handleTooltipShow(`${item.date}: ${formatCurrency(item.value)}`, e)} onMouseLeave={handleTooltipHide} data-id="element-2415"></div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate w-full text-center" data-id="element-2416">
                      {item.date}
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="absolute bottom-6 left-0 right-0 flex justify-between px-3" data-id="element-2417">
              <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2418">
                {formatCurrency(Math.min(...currentTransactionData.map(d => d.value)))}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2419">
                {formatCurrency(Math.max(...currentTransactionData.map(d => d.value)))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-between text-sm" data-id="element-2420">
            <div data-id="element-2421">
              <span className="text-gray-500 dark:text-gray-400" data-id="element-2422">
                Total Volume:
              </span>
              <span className="ml-1 font-medium" data-id="element-2423">
                {formatCurrency(currentTransactionData.reduce((sum, item) => sum + item.value, 0))}
              </span>
            </div>
            <div data-id="element-2424">
              <span className="text-gray-500 dark:text-gray-400" data-id="element-2425">Average:</span>
              <span className="ml-1 font-medium" data-id="element-2426">
                {formatCurrency(currentTransactionData.reduce((sum, item) => sum + item.value, 0) / currentTransactionData.length)}
              </span>
            </div>
            <div data-id="element-2427">
              <span className={`font-medium ${currentTransactionData[currentTransactionData.length - 1].value > currentTransactionData[0].value ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-2428">
                {currentTransactionData[currentTransactionData.length - 1].value > currentTransactionData[0].value ? '+' : '-'}
                {Math.abs(Math.round((currentTransactionData[currentTransactionData.length - 1].value - currentTransactionData[0].value) / currentTransactionData[0].value * 100))}
                %
              </span>
              <span className="ml-1 text-gray-500 dark:text-gray-400" data-id="element-2429">
                growth
              </span>
            </div>
          </div>
        </Card>
        <Card data-id="element-2430">
          <div className="flex justify-between items-center mb-4" data-id="element-2431">
            <div className="flex items-center" data-id="element-2432">
              <h3 className="font-medium" data-id="element-2433">Regional Distribution</h3>
              <button className="ml-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400" onMouseEnter={e => handleTooltipShow('Shows merchant distribution by region. Click on a region to see details.', e)} onMouseLeave={handleTooltipHide} data-id="element-2434">
                <HelpCircleIcon className="h-4 w-4" data-id="element-2435" />
              </button>
            </div>
            <div className="flex items-center space-x-2" data-id="element-2436">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2437">
                <MapPinIcon className="h-4 w-4 mr-1" data-id="element-2438" />
                View Map
              </button>
              <button className="p-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" data-id="element-2439">
                <PieChartIcon className="h-4 w-4" data-id="element-2440" />
              </button>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-2441">
            <div className="text-center w-full relative" data-id="element-2442">
              <div className="w-44 h-44 mx-auto relative" data-id="element-2443">
                <div className="absolute inset-0 rounded-full border-8 border-gray-100 dark:border-gray-700" data-id="element-2444"></div>
                {regionData.map((region, index) => {
                const startAngle = index > 0 ? regionData.slice(0, index).reduce((sum, r) => sum + r.value, 0) * 3.6 : 0;
                const endAngle = startAngle + region.value * 3.6;
                return <div key={region.name} className={`absolute inset-0 cursor-pointer transition-all duration-200 ${selectedRegion === region.name ? 'scale-105 z-10' : ''}`} onClick={() => handleRegionClick(region.name)} onMouseEnter={e => handleTooltipShow(`${region.name}: ${region.value}% (${region.merchants.toLocaleString()} merchants)`, e)} onMouseLeave={handleTooltipHide} style={{
                  clipPath: `conic-gradient(from ${startAngle}deg, transparent ${endAngle}deg, transparent 360deg)`
                }} data-id="element-2445">
                      <div className="w-full h-full rounded-full" style={{
                    background: `conic-gradient(from ${startAngle}deg, ${getRegionColor(index)} ${startAngle}deg ${endAngle}deg, transparent ${endAngle}deg)`
                  }} data-id="element-2446"></div>
                    </div>;
              })}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2447">
                  <div className="bg-white dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center shadow-sm" data-id="element-2448">
                    <div className="text-center" data-id="element-2449">
                      <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2450">
                        Total
                      </div>
                      <div className="font-medium" data-id="element-2451">
                        {merchantStats.total.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2452">
                        Merchants
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="absolute top-0 right-0 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 text-xs" data-id="element-2453">
                <div className="font-medium mb-1 text-gray-700 dark:text-gray-300" data-id="element-2454">
                  Legend
                </div>
                {regionData.map((region, index) => <div key={region.name} className="flex items-center mb-1 last:mb-0" data-id="element-2455">
                    <div className="w-2 h-2 rounded-full mr-1" style={{
                  backgroundColor: getRegionColor(index)
                }} data-id="element-2456"></div>
                    <span data-id="element-2457">{region.name}</span>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="mt-4" data-id="element-2458">
            {regionData.map((region, index) => <div key={region.name} className={`flex items-center justify-between mb-2 p-1.5 rounded cursor-pointer transition-colors
                  ${selectedRegion === region.name ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`} onClick={() => handleRegionClick(region.name)} data-id="element-2459">
                <div className="flex items-center" data-id="element-2460">
                  <div className="w-3 h-3 rounded-full mr-2" style={{
                backgroundColor: getRegionColor(index)
              }} data-id="element-2461"></div>
                  <span className="text-sm" data-id="element-2462">{region.name}</span>
                </div>
                <div className="flex items-center" data-id="element-2463">
                  <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2" data-id="element-2464">
                    <div className="h-2.5 rounded-full" style={{
                  width: `${region.value}%`,
                  backgroundColor: getRegionColor(index)
                }} data-id="element-2465"></div>
                  </div>
                  <span className="text-sm font-medium" data-id="element-2466">{region.value}%</span>
                </div>
              </div>)}
          </div>
          {getRegionDetails()}
        </Card>
      </div>

      {/* Pending Actions and Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-2467">
        <Card title="Pending Actions" data-id="element-2468">
          <div className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2469">
            {pendingActions.map(action => <div key={action.id} className="py-3 first:pt-0 last:pb-0" data-id="element-2470">
                <div className="flex justify-between" data-id="element-2471">
                  <div data-id="element-2472">
                    <p className="font-medium" data-id="element-2473">{action.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2474">
                      {action.type}
                    </p>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-2475">
                    <StatusBadge status={action.status === 'urgent' ? 'danger' : 'warning'} text={action.status.toUpperCase()} data-id="element-2476" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2477">
                      {action.time}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-2478">
            <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-2479">
              View all pending actions
              <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2480" />
            </button>
          </div>
        </Card>
        <Card data-id="element-2481">
          <div className="flex justify-between items-center mb-4" data-id="element-2482">
            <div className="flex items-center" data-id="element-2483">
              <h3 className="font-medium" data-id="element-2484">Payment Method Distribution</h3>
              <button className="ml-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400" onMouseEnter={e => handleTooltipShow('Shows payment method usage distribution. Click on a method to see details.', e)} onMouseLeave={handleTooltipHide} data-id="element-2485">
                <HelpCircleIcon className="h-4 w-4" data-id="element-2486" />
              </button>
            </div>
            <div className="flex items-center space-x-2" data-id="element-2487">
              <select className="text-xs border border-gray-300 dark:border-gray-600 rounded-md p-1 bg-white dark:bg-gray-800" data-id="element-2488">
                <option data-id="element-2489">Last 30 days</option>
                <option data-id="element-2490">Last 7 days</option>
                <option data-id="element-2491">Last 24 hours</option>
              </select>
              <button className="p-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" data-id="element-2492">
                <PieChartIcon className="h-4 w-4" data-id="element-2493" />
              </button>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-2494">
            <div className="w-full relative" data-id="element-2495">
              <div className="w-44 h-44 mx-auto relative" data-id="element-2496">
                <div className="absolute inset-0 rounded-full border-8 border-gray-100 dark:border-gray-700" data-id="element-2497"></div>
                {paymentMethodData.map((method, index) => {
                const startAngle = index > 0 ? paymentMethodData.slice(0, index).reduce((sum, m) => sum + m.percentage, 0) * 3.6 : 0;
                const endAngle = startAngle + method.percentage * 3.6;
                return <div key={method.method} className={`absolute inset-0 cursor-pointer transition-all duration-200 ${selectedPaymentMethod === method.method ? 'scale-105 z-10' : ''}`} onClick={() => handlePaymentMethodClick(method.method)} onMouseEnter={e => handleTooltipShow(`${method.method}: ${method.percentage}% (${method.transactions.toLocaleString()} transactions)`, e)} onMouseLeave={handleTooltipHide} style={{
                  clipPath: `conic-gradient(from ${startAngle}deg, transparent ${endAngle}deg, transparent 360deg)`
                }} data-id="element-2498">
                      <div className="w-full h-full rounded-full" style={{
                    background: `conic-gradient(from ${startAngle}deg, ${getPaymentMethodColor(index)} ${startAngle}deg ${endAngle}deg, transparent ${endAngle}deg)`
                  }} data-id="element-2499"></div>
                    </div>;
              })}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2500">
                  <div className="bg-white dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center shadow-sm" data-id="element-2501">
                    <div className="text-center" data-id="element-2502">
                      <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2503">
                        Volume
                      </div>
                      <div className="font-medium text-sm" data-id="element-2504">
                        {formatCurrency(transactionStats.volume)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2505">
                        {transactionStats.count.toLocaleString()} transactions
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Legend */}
              <div className="absolute top-0 right-0 bg-white dark:bg-gray-800 p-2 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 text-xs" data-id="element-2506">
                <div className="font-medium mb-1 text-gray-700 dark:text-gray-300" data-id="element-2507">
                  Legend
                </div>
                {paymentMethodData.map((method, index) => <div key={method.method} className="flex items-center mb-1 last:mb-0" data-id="element-2508">
                    <div className="w-2 h-2 rounded-full mr-1" style={{
                  backgroundColor: getPaymentMethodColor(index)
                }} data-id="element-2509"></div>
                    <span data-id="element-2510">{method.method}</span>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-2511">
            {paymentMethodData.map((method, index) => <div key={method.method} className={`flex items-center p-1.5 rounded cursor-pointer transition-colors
                  ${selectedPaymentMethod === method.method ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`} onClick={() => handlePaymentMethodClick(method.method)} data-id="element-2512">
                <div className="w-3 h-3 rounded-full mr-2" style={{
              backgroundColor: getPaymentMethodColor(index)
            }} data-id="element-2513"></div>
                <div data-id="element-2514">
                  <span className="text-sm" data-id="element-2515">{method.method}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" data-id="element-2516">
                    ({method.percentage}%)
                  </span>
                </div>
                <span className="ml-auto text-xs text-green-600 dark:text-green-400" data-id="element-2517">
                  +{method.growth}%
                </span>
              </div>)}
          </div>
          {getPaymentMethodDetails()}
        </Card>
      </div>
      {/* Tooltip */}
      {showTooltip.visible && <div className="fixed z-50 bg-gray-900 text-white text-xs rounded py-1 px-2 pointer-events-none" style={{
      left: `${showTooltip.x + 10}px`,
      top: `${showTooltip.y - 20}px`,
      maxWidth: '200px'
    }} data-id="element-2518">
          {showTooltip.content}
        </div>}
    </div>;
};
// Helper function to get region color
function getRegionColor(index: number): string {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#10b981' // emerald-500
  ];
  return colors[index % colors.length];
}
// Helper function to get payment method color
function getPaymentMethodColor(index: number): string {
  const colors = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981' // emerald-500
  ];
  return colors[index % colors.length];
}
export default Dashboard;